import { useCallback, useMemo } from "react";
import { ColumnResizedEvent } from "ag-grid-community/dist/lib/events";
import { ColumnApi, ColumnVisibleEvent, GridApi } from "ag-grid-community";
import _ from "lodash";
import { IMAGE_URL } from "constants/product-collection";
import { GridViewStoredState, ToolPanelState } from "helpers/porduct-collection-types";
import { debounceWithCalls } from "helpers/objUtils";
import { getPivotSettings } from "../helpers/sizingViewHelper";
import { reorderColumnKeys } from "../helpers/columnsHelper";
import { COLUMN_FIELDS } from "../../../constants/sizingView";

interface SizeGridHandlersProps {
    gridColumnApi: ColumnApi;
    gridApi: GridApi;
    storedState?: GridViewStoredState;
    onStoredStateChange?: (state: GridViewStoredState) => void;
}

const useGridStateHandlers = (props: SizeGridHandlersProps) => {
    const { gridColumnApi, gridApi, storedState, onStoredStateChange } = props;

    const saveState = useMemo(
        () => debounceWithCalls((updates) => onStoredStateChange(_.merge({}, ..._.flatten(updates))), 100),
        [onStoredStateChange],
    );

    const handleColumnPivotModeChanged = useCallback(
        () => saveState({ pivot: getPivotSettings(gridColumnApi) }),
        [gridColumnApi, saveState],
    );

    const handleColumnsChange = useCallback(() => {
        saveState({
            columns: gridColumnApi.getColumnState(),
            pivot: getPivotSettings(gridColumnApi),
        });
    }, [gridColumnApi, saveState]);

    const handleFilterChange = useCallback(() => {
        const filters = gridApi.getFilterModel();
        if (!_.isEqual(storedState.filterModel || {}, filters)) {
            saveState({ filterModel: _.isEmpty(filters) ? null : filters });
        }
    }, [gridApi, storedState?.filterModel, saveState]);

    const handleColumnResized = useCallback(
        ({ columns, finished }: ColumnResizedEvent) => finished && columns && handleColumnsChange(),
        [handleColumnsChange],
    );

    const handleToolPanelChange = useCallback(() => {
        const toolPanelId = gridApi.getOpenedToolPanel();
        if (toolPanelId) {
            switch (toolPanelId) {
                case "filters": {
                    // TODO: in ag-grid, IToolPanel has nothing common with IFiltersToolPanel that the Filters ToolPanel instance is. Will fix here once the respective Ag-Grid issue is resolved.
                    const filtersToolPanel: any = gridApi.getToolPanelInstance(toolPanelId);
                    saveState({
                        toolpanel: {
                            id: "filters",
                            expandedFilters: filtersToolPanel.filtersToolPanelListPanel.filterGroupComps
                                .filter((filterComp) => filterComp.isExpanded())
                                .map((filterComp) => filterComp.getColumns()[0].colId),
                        },
                    });
                    break;
                }
                default:
                    saveState({ toolpanel: { id: toolPanelId } as ToolPanelState }); // TODO: until the typescript 4.9 satisfies operator, need to cast.
            }
        } else {
            saveState({ toolpanel: null });
        }
    }, [gridApi, saveState]);

    const handleColumnVisible = useCallback(
        ({ columns }: ColumnVisibleEvent) => {
            // We are forcing image column to always be visible at the end of this function, thus triggering this handler.
            // In that case, skip it.
            if (!(_.size(columns) === 1 && _.head(columns).getColId() === IMAGE_URL)) {
                handleColumnsChange();
            }
        },
        [handleColumnsChange],
    );

    const onApplyGridStateChange = useCallback(() => {
        gridApi?.onFilterChanged();
        gridApi?.onSortChanged();
        // the bellow is required to override an internal bug that adds a gap at the top of the grid
        // on applying the modified column state
        gridApi?.redrawRows();
    }, [gridApi]);

    const onReorderGrouping = useCallback(
        (orderedKeys: string[]) => {
            const ordered = reorderColumnKeys(_.map(gridColumnApi.getRowGroupColumns(), "colId"), orderedKeys);
            gridColumnApi.setRowGroupColumns(ordered);
        },
        [gridColumnApi],
    );

    const onResetGrid = useCallback(() => {
        gridColumnApi?.resetColumnState();
        gridColumnApi?.resetColumnGroupState();
        gridApi?.setFilterModel(null);
        gridColumnApi.setPivotMode(false);
        gridColumnApi.setPivotColumns([]);
        onReorderGrouping([COLUMN_FIELDS.SHIPMENT, COLUMN_FIELDS.LOCATION]);
        onApplyGridStateChange();
    }, [gridApi, gridColumnApi, onApplyGridStateChange, onReorderGrouping]);

    const onSetGridState = useCallback(
        (state: GridViewStoredState) => {
            const pivot = state?.pivot;
            if (pivot) {
                gridColumnApi.setPivotMode(pivot?.mode || false);
                gridColumnApi.setPivotColumns(pivot?.columns || []);
            }

            if (state?.filterModel) {
                gridApi.setFilterModel(state.filterModel);
            }

            if (state?.columns) {
                gridColumnApi.applyColumnState({ state: state.columns, applyOrder: true });
            }

            if (state?.toolpanel) {
                gridApi.openToolPanel(state.toolpanel.id);
            }

            // TODO: use IFiltersToolPanel once Ag-Grid fixes typings
            if (state?.toolpanel?.id === "filters") {
                (gridApi.getToolPanelInstance("filters") as any).expandFilters(state?.toolpanel.expandedFilters);
            }

            onApplyGridStateChange();
        },
        [gridApi, gridColumnApi, onApplyGridStateChange],
    );

    const onFirstDataRendered = useCallback(() => {
        onSetGridState(storedState);
        onReorderGrouping([COLUMN_FIELDS.SHIPMENT, COLUMN_FIELDS.LOCATION]);
    }, [onReorderGrouping, onSetGridState, storedState]);

    return {
        onFilterChanged: handleFilterChange,
        onColumnPinned: handleColumnsChange,
        onColumnMoved: handleColumnsChange,
        onSortChanged: handleColumnsChange,
        onColumnValueChanged: handleColumnsChange,
        onColumnPivotChanged: handleColumnsChange,
        onColumnRowGroupChanged: handleColumnsChange,
        onColumnVisible: handleColumnVisible,
        onColumnResized: handleColumnResized,
        onToolPanelVisibleChanged: handleToolPanelChange,
        onColumnPivotModeChanged: handleColumnPivotModeChanged,
        onFirstDataRendered,
        onResetGrid,
        onSetGridState,
        onReorderGrouping,
    };
};

export default useGridStateHandlers;
